import { Injectable } from '@angular/core';

/**
 * A single column descriptor as consumed by the `ui-table-column-settings`
 * component and persisted (by key + visibility + order) at user level.
 *
 * `label` is an i18n key — the settings component runs it through the
 * `translate` pipe. Host components should pass the role-allowed columns only;
 * role-gated columns must be filtered out before reaching here.
 */
export interface ITableColumnSetting {
  /** Stable identifier persisted to storage. Must be unique within a table. */
  key: string;
  /** i18n key, resolved by the settings component. */
  label: string;
  /** Whether the column is currently shown. */
  visible: boolean;
  /**
   * Pinned columns are anchored to the front of the table: they cannot be
   * reordered and no other column can be placed before them.
   */
  pinned?: boolean;
  /** When `false`, the column cannot be hidden. Defaults to hideable. */
  hideable?: boolean;
}

/** Per-table persisted UI state. */
export interface ITableUiState {
  /** Map of column key → visible. */
  columns?: Record<string, boolean>;
  /** Ordered list of column keys (visible-first, hidden appended). */
  order?: string[];
  updated_at?: string;
}

interface IUiTablesStorage {
  tables: Record<string, ITableUiState>;
}

/** One-time migration flags, keyed by an arbitrary migration id (see resetVisibilityOnce). */
type IMigrationFlags = Record<string, boolean>;

/**
 * Single source of truth for per-user table column preferences (visibility +
 * order), persisted to `localStorage` under one shared key. Replaces the
 * read/write/restore logic that was previously copy-pasted into each table
 * component.
 *
 * Persistence is intentionally localStorage-only for now; all reads/writes are
 * funnelled through this service so the storage tier can be swapped later
 * without touching any table component.
 */
@Injectable({ providedIn: 'root' })
export class TablePreferencesService {
  private static readonly STORAGE_KEY = 'wolsal.ui.tables';
  private static readonly MIGRATIONS_STORAGE_KEY = 'wolsal.ui.tables.migrations';

  /** Raw persisted state for a single table (empty object if none/invalid). */
  getTableState(tableKey: string): ITableUiState {
    const storage = this.read();
    return storage.tables[tableKey] ?? {};
  }

  /**
   * Resolve the column order for a table: saved order first (stale keys
   * dropped), then any keys missing from storage appended in their natural
   * definition order. Safe against columns being added/removed in code later.
   */
  resolveOrder(tableKey: string, allKeys: string[]): string[] {
    const saved = this.getTableState(tableKey).order ?? [];
    const known = new Set(allKeys);
    const ordered = saved.filter((key) => known.has(key));
    const seen = new Set(ordered);
    allKeys.forEach((key) => {
      if (!seen.has(key)) {
        ordered.push(key);
      }
    });
    return ordered;
  }

  /**
   * Apply persisted visibility + order onto a set of default column
   * descriptors, returning a fresh ordered array of clones. Locked columns are
   * always forced visible. Call this on init to hydrate a table's columns.
   */
  applyPreferences(tableKey: string, defaults: ITableColumnSetting[]): ITableColumnSetting[] {
    const state = this.getTableState(tableKey);
    const byKey = new Map<string, ITableColumnSetting>(
      defaults.map((column) => [column.key, { ...column }])
    );

    if (state.columns) {
      byKey.forEach((column, key) => {
        if (column.hideable === false) {
          column.visible = true;
          return;
        }
        const saved = state.columns?.[key];
        if (saved !== undefined) {
          column.visible = !!saved;
        }
      });
    }

    return this.resolveOrder(tableKey, defaults.map((column) => column.key))
      .map((key) => byKey.get(key))
      .filter((column): column is ITableColumnSetting => !!column);
  }

  /**
   * Persist the order derived from the given ordered columns, and update
   * stored visibility.
   *
   * `changedVisibility`, when passed, restricts the visibility write to just
   * those keys (merged onto whatever's already stored) -- columns the user
   * didn't actually touch keep falling through to the computed default
   * (`buildDefaultColumnSettings`) on every load, so a company-level default
   * (e.g. "hide pricing columns") keeps applying to them. Omitting it persists
   * every column's current visibility (legacy behavior) -- avoid this for new
   * callers, since it bakes in ambient/default visibility as if the user had
   * deliberately chosen it, defeating future company-default changes for
   * columns nobody actually decided about.
   */
  save(tableKey: string, columns: ITableColumnSetting[], changedVisibility?: Record<string, boolean>): void {
    const storage = this.read();
    const columnsState: Record<string, boolean> = { ...(storage.tables[tableKey]?.columns ?? {}) };

    if (changedVisibility) {
      Object.entries(changedVisibility).forEach(([key, visible]) => {
        columnsState[key] = visible;
      });
    } else {
      columns.forEach((column) => {
        columnsState[column.key] = column.hideable === false ? true : !!column.visible;
      });
    }

    storage.tables[tableKey] = {
      columns: columnsState,
      order: columns.map((column) => column.key),
      updated_at: new Date().toISOString(),
    };

    this.write(storage);
  }

  /**
   * One-time cleanup of stored visibility for `keys` on `tableKey`, gated by
   * `migrationId` so it runs at most once per browser. Used to let a new
   * company-level default (e.g. "hide pricing columns", WB-6463) take effect
   * for users whose stored prefs predate the setting -- those entries were
   * never a deliberate choice about that column, just whatever the table
   * looked like by default at the time. Deletes (not sets false) so the
   * column falls through to the freshly-computed default on next read; a
   * later deliberate user toggle is unaffected since this never runs again.
   */
  resetVisibilityOnce(tableKey: string, keys: string[], migrationId: string): void {
    const flags = this.readMigrationFlags();
    if (flags[migrationId]) return;

    const storage = this.read();
    const state = storage.tables[tableKey];
    if (state?.columns) {
      keys.forEach((key) => delete state.columns![key]);
      this.write(storage);
    }

    flags[migrationId] = true;
    this.writeMigrationFlags(flags);
  }

  /**
   * One-time rename of stored column keys on `tableKey`, gated by
   * `migrationId` so it runs at most once per browser. Used when a column key
   * is renamed in code (e.g. `product_name` -> `product`, WB-7903) so users
   * keep the visibility and order they had chosen instead of silently falling
   * back to the defaults. Renames that would collide with a key already stored
   * are skipped, leaving the existing entry untouched.
   */
  renameKeysOnce(tableKey: string, mapping: Record<string, string>, migrationId: string): void {
    const flags = this.readMigrationFlags();
    if (flags[migrationId]) return;

    const storage = this.read();
    const state = storage.tables[tableKey];
    if (state) {
      // Resolved up front against the *stored* keys: `state.columns` is
      // rewritten below, so deciding per lookup would make the order pass see
      // the already-renamed keys and treat every one of them as a collision.
      const stored = state.columns ?? {};
      const rename = (key: string): string => {
        const renamed = mapping[key];
        return renamed && stored[renamed] === undefined ? renamed : key;
      };

      if (state.columns) {
        state.columns = Object.entries(state.columns).reduce<Record<string, boolean>>(
          (columns, [key, visible]) => {
            columns[rename(key)] = visible;
            return columns;
          },
          {}
        );
      }
      if (state.order) {
        state.order = state.order.map(rename);
      }
      this.write(storage);
    }

    flags[migrationId] = true;
    this.writeMigrationFlags(flags);
  }

  private read(): IUiTablesStorage {
    try {
      const raw = localStorage.getItem(TablePreferencesService.STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (parsed && typeof parsed === 'object' && parsed.tables) {
        return parsed as IUiTablesStorage;
      }
    } catch {
      // fall through to empty state on malformed JSON / disabled storage
    }
    return { tables: {} };
  }

  private write(storage: IUiTablesStorage): void {
    try {
      localStorage.setItem(TablePreferencesService.STORAGE_KEY, JSON.stringify(storage));
    } catch {
      // ignore quota / disabled storage errors — preferences are best-effort
    }
  }

  private readMigrationFlags(): IMigrationFlags {
    try {
      const raw = localStorage.getItem(TablePreferencesService.MIGRATIONS_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (parsed && typeof parsed === 'object') {
        return parsed as IMigrationFlags;
      }
    } catch {
      // fall through to empty state on malformed JSON / disabled storage
    }
    return {};
  }

  private writeMigrationFlags(flags: IMigrationFlags): void {
    try {
      localStorage.setItem(TablePreferencesService.MIGRATIONS_STORAGE_KEY, JSON.stringify(flags));
    } catch {
      // ignore quota / disabled storage errors — migration flags are best-effort
    }
  }
}
